import { Autocomplete, TextField } from "@mui/material";
import { Person2Outlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { useGetAssigneesQuery } from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks.api";
import { autocompleteSx, sharedInputProps, sharedMenuProps } from "@/styles/sharedComponentStyles";

interface AssigneeOption {
	firstName: string;
	lastName: string;
	assigneeId: number | null;
}

interface AssigneeSelectProps {
	value: number | null | undefined;
	onChange: (assigneeId: number | null) => void;
}

/** The assignee picker as it appears in both detail drawers. */
export default function AssigneeSelect({ value, onChange }: AssigneeSelectProps) {
	const { t } = useTranslation();
	const { data: users } = useGetAssigneesQuery();

	const unassignedOption: AssigneeOption = {
		firstName: t("createTask.unassigned"),
		lastName: "",
		assigneeId: null,
	};

	const options: AssigneeOption[] = [
		unassignedOption,
		...(users?.map((user) => ({
			firstName: user.firstName,
			lastName: user.lastName,
			assigneeId: +user.id,
		})) ?? []),
	].filter((option) => option?.firstName || option?.lastName);

	return (
		<Autocomplete<AssigneeOption, false, true, false>
			disableClearable
			className="w-full"
			getOptionLabel={(option) => `${option?.firstName ?? ""} ${option?.lastName ?? ""}`.trim()}
			isOptionEqualToValue={(a, b) => a?.assigneeId === b?.assigneeId}
			options={options}
			renderInput={(params) => (
				<TextField
					{...params}
					InputProps={{
						...params.InputProps,
						startAdornment: (
							<>
								<Person2Outlined sx={{ color: "action.active", fontSize: 18, mr: 0.5 }} />
								{params.InputProps.startAdornment}
							</>
						),
					}}
					inputProps={{ ...params.inputProps, ...sharedInputProps }}
				/>
			)}
			size="small"
			slotProps={{ paper: { sx: sharedMenuProps.PaperProps.sx } }}
			sx={autocompleteSx}
			value={options.find((option) => option?.assigneeId === value) ?? unassignedOption}
			onChange={(_, selected) => onChange(selected?.assigneeId ?? null)}
		/>
	);
}
